import { Injectable } from '@angular/core';
import { FrameworkVariableService } from '../frmVariableService/framework-variable.service';
import { EventBusPipe } from '../../eventbus/frm-eventpipe';
import { Subject } from 'rxjs';
import { FrmSubjectOptions } from '../../shared/frm-util';

@Injectable({
    providedIn: 'root'
})
export class BroadcastingStationService {

    private subjectMaps: Map<string, EventBusPipe>;
    private frmVariableService: FrameworkVariableService;
    private frmSubject = 'frmBroadcast-subjects';

    constructor() {
        this.frmVariableService = new FrameworkVariableService();
        this.subjectMaps = this.frmVariableService.getFrmVariableByKey(this.frmSubject) || new Map<string, EventBusPipe>();
    }

    /**
     * 注册主题，并获取当前主题唯一标识token
     * @param subjectCode 主题编码，建议有明确含义，方便确认当前主题的职责
     * code不是唯一标识，即使相同code每次注册都会生成一个新的主题，返回值token是唯一标识
     * @param options 主题注册操作，可以直接对于菜单 或 应用注册主题
     * @param customSub 自定义主题，如果为null或者不传，默认为Subject
     */
    public subjectRegister(subjectCode: string, options: FrmSubjectOptions, customSub?: Subject<any>): string {
        if (!customSub) {
            customSub = new Subject<any>();
        }
        let token: string;
        if (options && options.funcId) {
            token = options.funcId;
        } else if (options && options.appId && options.appEntrance) {
            token = `${options.appId}-${options.appEntrance}`;
        } else {
            token = this.createToken();
        }
        const pipe = new EventBusPipe(token, null, customSub);
        pipe.pipeCode = subjectCode;
        this.subjectMaps.set(token, pipe);
        this.frmVariableService.frameworkVariableReg(this.frmSubject, this.subjectMaps);
        return token;
    }

    /**
     * 注册主题，可直接获取主题实体，
     * 框架不托管维护，常用于适配层上事件扩展机制
     * @param options 注册参数
     * @param customSub 是否自定义主题
     */
    public customSubjectRegister(options: FrmSubjectOptions, customSub?: Subject<any>): Map<string, EventBusPipe> {
        const subMap = new Map<string, EventBusPipe>();
        if (!customSub) {
            customSub = new Subject<any>();
        }
        let token: string;
        if (options && options.customToken) {
            token = options.customToken;
        } else if (options && options.funcId) {
            token = options.funcId;
        } else if (options && options.appId && options.appEntrance) {
            token = `${options.appId}-${options.appEntrance}`;
        } else {
            token = this.createToken();
        }
        const pipe = new EventBusPipe(token, null, customSub);
        subMap.set(token, pipe);
        return subMap;
    }

    /**
     * 删除主题
     * @param subjectToken 主题唯一标识
     */
    public subjectRemove(subjectToken: string) {
        const pipe = this.subjectMaps.get(subjectToken) as EventBusPipe;
        pipe.unSubscribe();
        pipe.complete();
        this.subjectMaps.set(subjectToken, null);
        this.subjectMaps.delete(subjectToken);
        this.frmVariableService.frameworkVariableReg(this.frmSubject, this.subjectMaps);
    }

    /**
     * 通过某个主题发送消息
     * @param subjectToken 主题唯一的token标识
     * @param info 要发送的消息
     */
    public notify(subjectToken: string, info: any) {
        const sub = this.getSubject(subjectToken);
        if (!sub) {
            return;
        }
        sub.post(info);
    }

    /**
     * 为主题添加观察者，并监听主题消息
     * @param subjectToken 主题token
     * @param observerToken 当前监听主题的观察者唯一标识token
     * @param handler 观察者监听到主题消息时要执行的动作
     */
    public response(subjectToken: string, observerToken: string, handler: (value: any) => void) {
        const sub = this.getSubject(subjectToken);
        if (!sub) {
            return;
        }
        sub.subscribeWithObkey(observerToken, handler);
    }

    /**
     * 观察者取消对主题的订阅
     * @param subjectToken 主题唯一标识
     * @param observerToken 观察者唯一标识
     */
    public responseUnSubscribe(subjectToken: string, observerToken: string) {
        const sub = this.getSubject(subjectToken);
        if (!sub) {
            return;
        }
        sub.unSubscribeWithObkey(observerToken);
    }

    private getSubject(subjectToken: string): EventBusPipe {
        return this.subjectMaps.get(subjectToken);
    }

    /**
     * 创建唯一token
     */
    private createToken() {
        // tslint:disable-next-line: only-arrow-functions
        const S4 = function () {
            // tslint:disable-next-line:no-bitwise
            return (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1);
        };
        return (S4() + S4() + '-' + S4() + '-' + S4() + '-' + S4() + '-' + S4() + S4() + S4());
    }
}
